import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useNavigate } from "react-router";
import { Plus, Edit2, Trash2, Settings } from "lucide-react";
import {
  searchCMDBApplications,
  createCMDBApplication,
  updateCMDBApplication,
  deleteCMDBApplication,
  CMDBApplication,
  ApplicationStatus,
  ApplicationCriticality,
  listOrgUnits,
  OrgUnit,
} from "../../api/frameworksApi";
import { useToastStore } from "../../stores/toastStore";
import {
  RBACButton,
  RBACAction,
  RBACMultiAction,
} from "../components/RBACButton";
import {
  Modal,
  FormField,
  FormInput,
  FormSelect,
  FormTextarea,
  ModalFooterButtons,
} from "../components/Modal";
import { DataTable, Column } from "../components/DataTable";
import { TableToolbar } from "../components/TableToolbar";
import { AppAccessReviewModal } from "./cmdb/components/AppAccessReviewModal";
import { StatusBadge, flattenOrgHierarchy } from "./cmdb/cmdbUtils";
import { OrgCascadeFilter } from "./cmdb/components/OrgCascadeFilter";
import { Tip } from "../components/ui/tooltip";
import { showToast } from "../components/ui/custom-toast";
import { useRBACPermission } from "../../hooks/useRBACPermission";

// Redundant constants removed - using cmdbUtils

// ─── Form State ──────────────────────────────────────────────────────────────

interface AppForm {
  name: string;
  appCode: string;
  description: string;
  parentId: string;
  status: ApplicationStatus | "";
  criticality: ApplicationCriticality | "";
}

const DEFAULT_FORM: AppForm = {
  name: "",
  appCode: "",
  description: "",
  parentId: "",
  status: "",
  criticality: "",
};

// ─── Component ───────────────────────────────────────────────────────────────

/**
 * Sortable column key -> backend field name sent as `sortByColumn` in
 * POST /cmdb/applications/{page}/{size}. Sorting is fully server-side; a key
 * with no entry here is forwarded as-is.
 */
const APP_SORT_COLUMNS: Record<string, string> = {
  name: "name",
  appCode: "appCode",
  organizationName: "organizationName",
  departmentName: "departmentName",
  divisionName: "divisionName",
  status: "status",
  criticality: "criticality",
  componentCount: "componentCount",
};

export function CMDBApplications() {
  const navigate = useNavigate();
  // Gates the Edit button inside the User Access Review popup.
  const canUpdateApp = useRBACPermission("cmdb", "update");
  /* ── List state ──
   * Search, sorting, filtering and pagination are all resolved by the backend
   * via POST /cmdb/applications/{page}/{size}; `applications` always holds
   * exactly the rows for the current page and is rendered as-is. */
  const [applications, setApplications] = useState<CMDBApplication[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  // The keyword actually sent to the backend (committed on Enter / icon click).
  const [keyword, setKeyword] = useState("");
  const [total, setTotal] = useState(0);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingApp, setEditingApp] = useState<CMDBApplication | null>(null);
  const [form, setForm] = useState<AppForm>(DEFAULT_FORM);

  // User Access Review popup (gear icon) — the popup loads and saves its own
  // config against the PAG review-config endpoints, keyed by this row's id.
  const [reviewApp, setReviewApp] = useState<CMDBApplication | null>(null);

  // Delete confirmation state
  const [deletingApp, setDeletingApp] = useState<CMDBApplication | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  // Create / Edit in-flight flag (block double-submit)
  const [submitting, setSubmitting] = useState(false);

  // All org units (flat) — used for both create picker and cascade filters
  const [parentOptions, setParentOptions] = useState<OrgUnit[]>([]);

  // Cascade filter state (toolbar)
  const [filterOrgId, setFilterOrgId] = useState("");
  const [filterDivId, setFilterDivId] = useState("");
  const [filterDeptId, setFilterDeptId] = useState("");

  // Cascade picker state (create form)
  const [formOrgId, setFormOrgId] = useState("");
  const [formDivId, setFormDivId] = useState("");
  const [formDeptId, setFormDeptId] = useState("");

  // The skeleton is only shown before the very first page ever lands; every
  // later fetch keeps the current rows visible (no blank page on paging).
  const firstLoadDoneRef = useRef(false);
  const loadApplications = useCallback(async () => {
    if (!firstLoadDoneRef.current) setLoading(true);
    try {
      const res = await searchCMDBApplications(page, pageSize, {
        keyword: keyword.trim(),
        // Unsorted (the initial state) sends both sort fields as empty.
        sortByColumn: sortKey ? (APP_SORT_COLUMNS[sortKey] ?? sortKey) : "",
        sortingOrder: sortKey ? sortDir : "",
        orgId: filterOrgId || null,
        divisionId: filterDivId || null,
        departmentId: filterDeptId || null,
      });
      setApplications(res.items);
      setTotal(res.totalItems);
      // The current page can fall off the end of the result set (a delete, a
      // larger page size, a narrower filter) — step back to the last real page.
      if (res.items.length === 0 && page > 0 && res.totalItems > 0) {
        setPage(Math.max(0, Math.ceil(res.totalItems / pageSize) - 1));
      }
    } catch (err) {
      useToastStore
        .getState()
        .showError(
          "Error",
          err instanceof Error ? err.message : "Failed to load applications",
        );
      setApplications([]);
      setTotal(0);
    } finally {
      firstLoadDoneRef.current = true;
      setLoading(false);
    }
  }, [
    page,
    pageSize,
    keyword,
    sortKey,
    sortDir,
    filterOrgId,
    filterDivId,
    filterDeptId,
  ]);

  useEffect(() => {
    loadApplications();
  }, [loadApplications]);

  useEffect(() => {
    loadParentOptions();
  }, []);

  useEffect(() => {
    setPage(0);
  }, [filterOrgId, filterDivId, filterDeptId]);

  /* ── Search submit ──
   * Typing alone never hits the API: the keyword is only pushed to the
   * backend on Enter / the search button, or the moment the box is cleared. */
  const submitSearch = (value: string) => {
    const next = value.trim();
    if (next === keyword) return;
    setKeyword(next);
    setPage(0);
  };

  const loadParentOptions = async () => {
    try {
      const res = await listOrgUnits();
      setParentOptions(
        flattenOrgHierarchy(res.data || [], [
          "ORGANIZATION",
          "DIVISION",
          "DEPARTMENT",
        ]),
      );
    } catch {
      // non-fatal
    }
  };

  // ─── Open Modal ──────────────────────────────────────────────────────────

  const openCreate = () => {
    setEditingApp(null);
    setForm(DEFAULT_FORM);
    setFormOrgId("");
    setFormDivId("");
    setFormDeptId("");
    setShowModal(true);
  };

  const openEdit = (app: CMDBApplication) => {
    setEditingApp(app);
    setForm({
      name: app.name,
      appCode: app.appCode || "",
      description: app.description || "",
      parentId: app.parentId || "",
      status: app.status,
      criticality: app.criticality,
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingApp(null);
    setForm(DEFAULT_FORM);
    setFormOrgId("");
    setFormDivId("");
    setFormDeptId("");
  };

  // ─── Submit ──────────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    if (submitting) return;
    if (!form.name.trim()) {
      useToastStore
        .getState()
        .showError("Error", "Application name is required");
      return;
    }
    if (!form.appCode.trim()) {
      showToast("error", "Application code is required");
      return;
    }
    if (!form.status) {
      useToastStore.getState().showError("Error", "Please select a status");
      return;
    }
    if (!form.criticality) {
      useToastStore.getState().showError("Error", "Please select a criticality");
      return;
    }
    if (!editingApp) {
      if (!form.parentId) {
        useToastStore
          .getState()
          .showError("Error", "Please select a Division as the parent");
        return;
      }
      // Backend does not validate parent type on POST /cmdb/applications;
      // hierarchy (DIVISION → APPLICATION) is only enforced on /org-units.
      const parent = parentOptions.find((o) => o.id === form.parentId);
      if (!parent || parent.orgType !== "DIVISION") {
        useToastStore
          .getState()
          .showError("Error", "Application parent must be a Division");
        return;
      }
    }

    setSubmitting(true);
    try {
      if (editingApp) {
        const updated = await updateCMDBApplication(editingApp.id, {
          name: form.name,
          appCode: form.appCode || null,
          description: form.description || null,
          status: form.status as ApplicationStatus,
          criticality: form.criticality as ApplicationCriticality,
        });
        // Merge over the previous row so PATCH responses that omit derived/joined
        // fields (departmentName, divisionName, path, componentCount) don't blank
        // out columns until the next full reload.
        setApplications((prev) =>
          prev.map((a) =>
            a.id === editingApp.id ? { ...a, ...updated.data } : a,
          ),
        );
        useToastStore.getState().showSuccess("Success", "Application updated");
      } else {
        await createCMDBApplication({
          name: form.name,
          appCode: form.appCode || null,
          description: form.description || null,
          parentId: form.parentId,
          status: form.status as ApplicationStatus,
          criticality: form.criticality as ApplicationCriticality,
        });
        useToastStore.getState().showSuccess("Success", "Application created");
        closeModal();
        // Reload from the server so the new row appears with its derived/joined
        // fields (departmentName, divisionName, componentCount) and in the
        // backend's newest-first order; jump to the first page so it's visible.
        setPage(0);
        await loadApplications();
        return;
      }
      closeModal();
    } catch (err) {
      useToastStore
        .getState()
        .showError(
          "Error",
          err instanceof Error ? err.message : "Operation failed",
        );
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Delete ──────────────────────────────────────────────────────────────
  const confirmDelete = async () => {
    if (!deletingApp) return;
    setDeleting(true);
    try {
      await deleteCMDBApplication(deletingApp.id);
      setApplications((prev) => prev.filter((a) => a.id !== deletingApp.id));
      useToastStore.getState().showSuccess("Success", "Application deleted.");
      setDeletingApp(null);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "";
      if (msg.includes("CMDB-0034-E")) {
        useToastStore
          .getState()
          .showError(
            "Error",
            "This application is linked to one or more System Security Plans and cannot be deleted. Remove the SSP association first.",
          );
      } else if (msg.includes("404")) {
        useToastStore.getState().showError("Error", "Application not found.");
      } else {
        useToastStore
          .getState()
          .showError("Error", "Failed to delete application");
      }
      setDeletingApp(null);
    } finally {
      setDeleting(false);
    }
  };

  // ─── Cascade derived lists ───────────────────────────────────────────────

  const orgOptions = useMemo(
    () => parentOptions.filter((o) => o.orgType === "ORGANIZATION"),
    [parentOptions],
  );

  /* Org hierarchy is ORGANIZATION → DEPARTMENT → DIVISION → APPLICATION
   * (see PARENT_TYPE_FOR in OrganizationManagement.tsx, which is what the
   * /org-units API enforces). Every cascade below walks that order: a
   * Department's parent is an Organization, a Division's parent is a
   * Department, and an Application's parent is a Division. */

  // Toolbar filter cascade  (Org → Dept → Div)
  const deptOptions = useMemo(
    () =>
      filterOrgId
        ? parentOptions.filter(
            (o) => o.orgType === "DEPARTMENT" && o.parentId === filterOrgId,
          )
        : [],
    [parentOptions, filterOrgId],
  );
  const divOptions = useMemo(
    () =>
      filterDeptId
        ? parentOptions.filter(
            (o) => o.orgType === "DIVISION" && o.parentId === filterDeptId,
          )
        : [],
    [parentOptions, filterDeptId],
  );

  // Form picker cascade  (Org → Dept → Div)
  // Department options: departments directly under the selected Organization
  const formDeptOptions = useMemo(
    () =>
      formOrgId
        ? parentOptions.filter(
            (o) => o.orgType === "DEPARTMENT" && o.parentId === formOrgId,
          )
        : [],
    [parentOptions, formOrgId],
  );
  // Division options: divisions directly under the selected Department
  const formDivOptions = useMemo(
    () =>
      formDeptId
        ? parentOptions.filter(
            (o) => o.orgType === "DIVISION" && o.parentId === formDeptId,
          )
        : [],
    [parentOptions, formDeptId],
  );

  /* ── Rows for the current page ──
   * No client-side filtering, sorting or slicing: the backend already applied
   * the keyword, sort column/order, org/division/department filters and the
   * page window. */
  const pagedApplications = applications;
  /** Serial number continues across pages (page 2 with 10/page starts at 11). */
  const appRowSerial = (index: number) => page * pageSize + index + 1;

  // ─── Sorting ─────────────────────────────────────────────────────────────

  const SORTABLE_KEYS = new Set([
    "name",
    "appCode",
    "organizationName",
    "departmentName",
    "divisionName",
    "status",
    "criticality",
    "componentCount",
  ]);

  const handleSort = (key: string) => {
    setPage(0);
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  // ─── Columns ─────────────────────────────────────────────────────────────

  const columns: Column<CMDBApplication>[] = [
    {
      key: "_sno",
      header: "#",
      width: "w-10",
      align: "center",
      render: (_, i) => (
        <span className="d text-sm">{appRowSerial(i)}</span>
      ),
    },
    {
      key: "name",
      header: "Application Name",
      align: "left",
      headerAlign: "center",
      render: (row) => <span className="font-medium">{row.name}</span>,
    },
    {
      key: "appCode",
      header: "App Code",
      align: "center",
      render: (row) =>
        row.appCode ? (
          <span className="inline-flex items-center justify-center w-20 px-2 py-0.5 rounded-md bg-muted text-xs font-mono text-foreground truncate">
            {row.appCode}
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      key: "organizationName",
      header: "Organization",
      align: "left",
      headerAlign: "center",
      render: (row) => (
        <span className="text-sm d">{row.organizationName || "—"}</span>
      ),
    },
    {
      key: "departmentName",
      header: "Department",
      align: "left",
      headerAlign: "center",
      render: (row) => (
        <span className="text-sm d">{row.departmentName || "—"}</span>
      ),
    },
    {
      key: "divisionName",
      header: "Division",
      align: "left",
      headerAlign: "center",
      render: (row) => (
        <span className="text-sm d">{row.divisionName || "—"}</span>
      ),
    },
    {
      key: "status",
      header: "Status",
      align: "center",
      render: (row) => <StatusBadge status={row.status} pill />,
    },
    {
      key: "criticality",
      header: "Criticality",
      align: "center",
      render: (row) => (
        <StatusBadge status={row.criticality} type="criticality" pill />
      ),
    },
    {
      key: "componentCount",
      header: "Components",
      align: "center",
      render: (row) => (
        <span className="inline-flex items-center justify-center min-w-[1.5rem] px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-400 text-xs font-medium">
          {row.componentCount ?? 0}
        </span>
      ),
    },
    // {
    //   key: "ssps",
    //   header: "SSP",
    //   align: "center",
    //   cellClassName: "text-left [&>div]:justify-start",
    //   render: (row) => {
    //     const ssps = row.ssps ?? [];
    //     if (ssps.length === 0) return <span className="d">—</span>;
    //     return (
    //       <div className="flex flex-col items-start gap-0.5">
    //         {ssps.map((s) => (
    //           <button
    //             key={s.sspId}
    //             onClick={(e) => { e.stopPropagation(); navigate(`/continuous-compliance/system/system-security-plan/${s.sspId}`); }}
    //             title={s.title}
    //             className="text-sm font-medium text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300 underline-offset-2 hover:underline max-w-[220px] truncate text-left"
    //           >
    //             {s.title}
    //           </button>
    //         ))}
    //       </div>
    //     );
    //   },
    // },
    {
      key: "_actions",
      header: "Actions",
      align: "center",
      width: "w-28",
      render: (row) => (
        <div className="flex items-center justify-center gap-1">
          <RBACAction module="cmdb" action="update">
            <Tip label="Edit Application" side="bottom">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  openEdit(row);
                }}
                className="p-1.5 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 rounded transition-colors"
              >
                <Edit2 className="size-4 d hover:text-indigo-600" />
              </button>
            </Tip>
          </RBACAction>
          <RBACAction module="cmdb" action="delete">
            <Tip label="Delete Application" side="bottom">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setDeletingApp(row);
                }}
                className="p-1.5 hover:bg-red-50 dark:hover:bg-red-500/10 rounded transition-colors"
              >
                <Trash2 className="size-4 d hover:text-red-600" />
              </button>
            </Tip>
          </RBACAction>
          <RBACAction module="cmdb" action="view">
            <Tip label="User Access Review" side="bottom">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setReviewApp(row);
                }}
                className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-500/10 rounded transition-colors"
              >
                <Settings className="size-4 d hover:text-slate-700 dark:hover:text-slate-200" />
              </button>
            </Tip>
          </RBACAction>
        </div>
      ),
    },
  ];

  const sortableColumns = columns.map((c) =>
    SORTABLE_KEYS.has(c.key) ? { ...c, sortable: true } : c,
  );

  // ─── Render ──────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Applications</h1>
          <p className="text-sm d mt-0.5">
            Manage CMDB applications, their status, criticality, and component
            mappings
          </p>
        </div>
        <div className="flex items-center gap-2 self-start sm:self-auto flex-shrink-0">
          <RBACMultiAction
            checks={[
              ["cmdb", "create"],
              ["cmdb", "update"],
            ]}
            type="any"
          >
            <button
              onClick={openCreate}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              <Plus className="size-4" />
              New Application
            </button>
          </RBACMultiAction>
        </div>
      </div>

      {/* Toolbar: Search + Cascade Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <TableToolbar
          search={{
            value: searchQuery,
            onChange: setSearchQuery,
            onSubmit: submitSearch,
            placeholder: "Search applications",
            ariaLabel: "Search applications",
          }}
          pageSize={pageSize}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(0);
          }}
          pageSizeLabel="Rows per page"
          trailing={
            <>
              <OrgCascadeFilter
                parentOptions={parentOptions}
                orgId={filterOrgId}
                divId={filterDivId}
                deptId={filterDeptId}
                onOrgChange={setFilterOrgId}
                onDivChange={setFilterDivId}
                onDeptChange={setFilterDeptId}
                disabled={loading}
              />

              {/* Clear filters */}
              {(filterOrgId || filterDivId || filterDeptId || searchQuery) && (
                <button
                  onClick={() => {
                    setFilterOrgId("");
                    setFilterDivId("");
                    setFilterDeptId("");
                    setSearchQuery("");
                    submitSearch("");
                  }}
                  className="h-[38px] flex items-center rounded-lg border border-border bg-card px-3 text-sm text-muted-foreground transition-colors hover:border-indigo-400/60 hover:text-foreground dark:hover:border-indigo-400/40"
                >
                  Clear
                </button>
              )}
            </>
          }
        />
      </div>

      <DataTable
        columns={sortableColumns}
        data={pagedApplications}
        loading={loading}
        emptyText="No applications found"
        page={page}
        pageSize={pageSize}
        totalItems={total}
        onPageChange={setPage}
        sortKey={sortKey}
        sortDir={sortDir}
        onSort={handleSort}
        onRowClick={(row) => navigate(`/cmdb/applications/${row.id}`)}
      />

      {/* User Access Review (gear icon) */}
      <AppAccessReviewModal
        // Remount per application so no draft state carries between rows.
        key={reviewApp?.id ?? "none"}
        open={!!reviewApp}
        onClose={() => setReviewApp(null)}
        applicationId={reviewApp?.id ?? ""}
        applicationName={reviewApp?.name ?? ""}
        canEdit={canUpdateApp}
      />

      {/* Create / Edit Modal */}
      <Modal
        open={showModal}
        onClose={closeModal}
        title={editingApp ? "Edit Application" : "New Application"}
        size="2xl"
        footer={
          <ModalFooterButtons
            onCancel={closeModal}
            onSubmit={handleSubmit}
            submitLabel="Save"
            submitDisabled={submitting}
          />
        }
      >
        <div className="space-y-3">
          {/* ── Section: Basic Information (all required) ── */}
          <div className="space-y-2.5">
            <p className="text-[10px] font-semibold tracking-widest d">
              Basic Information
            </p>
            {/* Row 1: Application Name + App Code (identity fields together, both required) */}
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Application Name" required>
                <FormInput
                  value={form.name}
                  onChange={(v) => setForm({ ...form, name: v })}
                  placeholder="e.g. Internet Banking Portal"
                />
              </FormField>
              <FormField label="Application Code" required>
                <FormInput
                  value={form.appCode}
                  onChange={(v) => setForm({ ...form, appCode: v })}
                  placeholder="e.g. IB, CBS, PAYROLL"
                />
              </FormField>
            </div>
            {/* Row 2: Status + Criticality (state fields together, both required) */}
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Status" required>
                <FormSelect
                  value={form.status}
                  onChange={(v) =>
                    setForm({ ...form, status: v as ApplicationStatus })
                  }
                  options={[
                    { value: "", label: "Select status" },
                    { value: "OPERATIONAL", label: "Operational" },
                    { value: "UNDER_DEVELOPMENT", label: "Under Development" },
                    { value: "DISPOSITION", label: "Disposition" },
                    { value: "OTHER", label: "Other" },
                  ]}
                />
              </FormField>
              <FormField label="Criticality" required>
                <FormSelect
                  value={form.criticality}
                  onChange={(v) =>
                    setForm({
                      ...form,
                      criticality: v as ApplicationCriticality,
                    })
                  }
                  options={[
                    { value: "", label: "Select criticality" },
                    { value: "LOW", label: "Low" },
                    { value: "MEDIUM", label: "Medium" },
                    { value: "HIGH", label: "High" },
                    { value: "CRITICAL", label: "Critical" },
                  ]}
                />
              </FormField>
            </div>
          </div>

          {/* ── Section: Organization Hierarchy (create only, all required) ── */}
          {!editingApp && (
            <div className="space-y-2.5 border-t border-border pt-3">
              <p className="text-[10px] font-semibold tracking-widest d">
                Organization Hierarchy
              </p>
              {/* Row 1: Organization (full width) */}
              <FormField label="Organization" required>
                <FormSelect
                  searchable
                  value={formOrgId}
                  onChange={(v) => {
                    setFormOrgId(v);
                    setFormDivId("");
                    setFormDeptId("");
                    setForm({ ...form, parentId: "" });
                  }}
                  options={[
                    { value: "", label: "Select Organization" },
                    ...orgOptions.map((o) => ({
                      value: o.id,
                      label: o.name,
                    })),
                  ]}
                />
              </FormField>
              {/* Row 2: Department (full width — requires Organization above to be picked first) */}
              <FormField
                label="Department"
                required
                hint={
                  formOrgId && !formDeptOptions.length
                    ? "This organization has no departments yet. Add one under Organization Management first."
                    : undefined
                }
              >
                <FormSelect
                  searchable
                  value={formDeptId}
                  onChange={(v) => {
                    setFormDeptId(v);
                    setFormDivId("");
                    setForm({ ...form, parentId: "" });
                  }}
                  options={[
                    {
                      value: "",
                      label: formOrgId
                        ? "Select Department"
                        : "Select an Organization first",
                    },
                    ...formDeptOptions.map((o) => ({
                      value: o.id,
                      label: o.name,
                    })),
                  ]}
                />
              </FormField>
              {/* Row 3: Division — the application's actual parent, so picking
                  one is what sets form.parentId. */}
              <FormField
                label="Division"
                required
                hint={
                  formDeptId && !formDivOptions.length
                    ? "This department has no divisions yet. Add one under Organization Management first."
                    : "Applications must be created under a Division"
                }
              >
                <FormSelect
                  searchable
                  value={formDivId}
                  onChange={(v) => {
                    setFormDivId(v);
                    setForm({ ...form, parentId: v });
                  }}
                  options={[
                    {
                      value: "",
                      label: formDeptId
                        ? "Select Division"
                        : "Select a Department first",
                    },
                    ...formDivOptions.map((o) => ({
                      value: o.id,
                      label: o.name,
                    })),
                  ]}
                />
              </FormField>
            </div>
          )}

          {/* ── Section: Additional (optional) ── */}
          <div className="border-t border-border pt-3">
            
            <FormField label="Description">
              <FormTextarea
                value={form.description}
                onChange={(v) => setForm({ ...form, description: v })}
                placeholder="Optional description"
                rows={3}
              />
            </FormField>
          </div>
        </div>
      </Modal>

      <Modal
        open={!!deletingApp}
        onClose={() => {
          if (!deleting) setDeletingApp(null);
        }}
        title="Delete Application?"
        size="md"
        footer={
          <ModalFooterButtons
            onCancel={() => setDeletingApp(null)}
            onSubmit={confirmDelete}
            submitLabel={deleting ? "Deleting" : "Delete"}
            submitDisabled={deleting}
          />
        }
      >
        <p className="text-sm d">
          This will permanently remove all CMDB data linked to this application
          - components, assets, cloud accounts, and service mappings. This
          cannot be undone.
        </p>
      </Modal>
    </div>
  );
}
